/**
 * Performance Wrapper Component
 * 
 * Wraps the entire application to provide performance monitoring
 * capabilities including route transitions and global performance tracking.
 */

import React, { useEffect, useState } from 'react';
import { useRoutePerformance } from '../../hooks/usePerformanceMonitor';
import { PerformanceDashboard } from '../debug/PerformanceDashboard';

interface PerformanceWrapperProps {
  children: React.ReactNode;
}

export const PerformanceWrapper: React.FC<PerformanceWrapperProps> = ({ children }) => {
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  
  // Enable route performance tracking
  useRoutePerformance();

  // Add keyboard shortcut for performance dashboard (Ctrl+Shift+P)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'P') {
        event.preventDefault();
        setIsDashboardOpen(true);
      }
      
      // Escape to close dashboard
      if (event.key === 'Escape' && isDashboardOpen) {
        setIsDashboardOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isDashboardOpen]);

  // Add performance dashboard toggle button in development or when explicitly enabled
  useEffect(() => {
    // Enable in development or when VITE_ENABLE_PERFORMANCE_MONITOR is set
    const isEnabled = import.meta.env.DEV ||
                     import.meta.env.VITE_ENABLE_PERFORMANCE_MONITOR === 'true';
    
    if (isEnabled) {
      const button = document.createElement('button');
      button.innerHTML = '📊';
      button.title = 'Open Performance Dashboard (Ctrl+Shift+P)\n\nPerformance Monitoring Active';
      button.style.cssText = `
        position: fixed;
        bottom: 20px;
        right: 20px;
        opacity: 0.8;
        width: 50px;
        height: 50px;
        border-radius: 50%;
        border: none;
        background: #3b82f6;
        color: white;
        font-size: 20px;
        cursor: pointer;
        z-index: 1000;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        transition: all 0.2s ease;
      `;
      
      button.addEventListener('mouseenter', () => {
        button.style.transform = 'scale(1.1)';
        button.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.3)';
      });
      
      button.addEventListener('mouseleave', () => {
        button.style.transform = 'scale(1)';
        button.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
      });
      
      button.addEventListener('click', () => {
        setIsDashboardOpen(true);
      });
      
      document.body.appendChild(button);
      
      return () => {
        if (document.body.contains(button)) {
          document.body.removeChild(button);
        }
      };
    }
  }, []);

  return (
    <>
      {children}
      
      {/* Performance Dashboard */}
      <PerformanceDashboard
        isOpen={isDashboardOpen}
        onClose={() => setIsDashboardOpen(false)}
      />
    </>
  );
};